import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  Label,
  LabeledControl,
  Paragraph,
  Radio,
  Tag,
  TextButton,
} from '@zillow/constellation-10';

import Modal from '@common/components/ConstellationV10Modal';
import userPersonas from '@common/constants/userPersonas';
import type { ILeasesForPropertyModel } from '@rental-manager/models/Types';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

type AdditionalLeaseSelectorProps = {
  index: number | null;
};

type LeaseSelectorProps = ILeasesForPropertyModel & AdditionalLeaseSelectorProps;

type Props = {
  currentProperty: ZrmClickstreamListing;
  leasesForProperty: Array<LeaseSelectorProps>;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (leaseId: number | null) => void;
  propertyId: string;
  user: ZrmClickstreamUser;
};

const LeaseSelectorPage = ({
  currentProperty,
  leasesForProperty,
  onBack,
  onClose,
  onSubmit,
  propertyId,
  user,
}: Props) => {
  // @ts-ignore
  const leaseId = useSelector((state) => state.payments.landlord.onboarding[propertyId].leaseId);
  const [selectedLease, setSelectedLease] = useState<number | null>(null);

  useEffect(() => {
    if (leaseId !== null) {
      setSelectedLease(leaseId);
    }
  }, [leaseId]);

  const leases: Array<LeaseSelectorProps> = leasesForProperty.map((lease, index) => ({
    ...lease,
    index,
  }));

  const handleSubmit = () => {
    onSubmit(selectedLease);
    IreneOnboardingCSEvents.emitContinueWithLeaseSelectedEvent({
      currentProperty,
      user,
    });
  };

  const onSkip = () => {
    onSubmit(null);
    IreneOnboardingCSEvents.emitContinueFromLeaseSelectorToAddPaymentManuallyEvent({
      currentProperty,
      user,
    });
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={onBack}>
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            Payment setup
          </Heading>
        </Box>
      }
      onRequestClose={onClose}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          <Paragraph textStyle="body-lg-bold">Select the lease you&apos;re setting up payments for</Paragraph>
          {(leases || []).map((lease) => {
            return (
              <LabeledControl
                key={lease.index}
                control={
                  <Radio checked={selectedLease === lease.index} onChange={() => setSelectedLease(lease.index)} />
                }
                label={
                  <Label css={{ marginTop: 'token(spacing.xs) !important' }}>
                    {`${lease.leaseTenants[0].firstName} ${lease.leaseTenants[0].lastName}'s Lease`}
                    <Tag
                      size="sm"
                      css={{ marginLeft: 'token(spacing.xs)', padding: 'token(spacing.xxs) !important' }}
                      tone="green"
                    >
                      {lease.status}
                    </Tag>
                  </Label>
                }
              />
            );
          })}
        </Box>
      }
      footer={
        <ButtonGroup
          css={{
            padding: 'token(spacing.sm) !important',
          }}
          aria-label="Footer"
        >
          <TextButton onClick={onSkip}>I don&apos;t have a lease in Zillow/Set up manually</TextButton>
          <Button onClick={handleSubmit} disabled={selectedLease === null} emphasis="filled">
            Continue
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default LeaseSelectorPage;
